import styled from "styled-components";
import { Button } from "../../App.styles";

const defaultWidth = 250;

export const getItemWidth = containerWidth => {
    const itemGap = 6;
    const itemWidth = containerWidth
        ? .32 * containerWidth - itemGap
        : defaultWidth;
    return `${Math.min(itemWidth, defaultWidth)}px`;
};

export const UploadBody = styled('label', {
    shouldForwardProp: (props) => !['container'].includes(props),
})(({ theme, container, isDisabled }) => {
    const itemWidth = getItemWidth(container?.clientWidth);
    
    return `
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: ${theme.rounded.md};
        border: 2px dashed ${theme.primaryColor};
        background: ${theme.cardColor}50;
        width: ${defaultWidth}px;
        height: 325px;
        position: absolute;
        bottom: 0;
        left: 0;
        cursor: ${isDisabled ? 'not-allowed' : 'pointer'};

        .svg-inline--fa:not([data-icon="question-circle"]) {
            width: 72px;
            height: 50px;
            color: ${theme.primaryColor};
            opacity: ${isDisabled ? 0.65 : 1};
        }

        .btn {
            border-radius: ${theme.rounded.xs};
            display: block;
            width: 100%;
            white-space: normal;

            span {
                text-transform: capitalize;
            }
        }

        small {
            margin-top: 0.5rem;
            display: block;
        }

        ${theme.breakpoints.up('sm')} {
            width: ${itemWidth};
            min-width: ${itemWidth};
            height: auto;
            position: relative;
            bottom: auto;
            right: auto;

            .btn.btn--flat {
                padding: .625rem .5rem .5rem .5rem;
            }
        }
    `;
});

export const ImageContainer = styled.div(({ theme }) => `
    width: 100%;
    max-width: 280px;
    overflow: auto;
    display: flex;

    ${theme.breakpoints.up('sm')} {
        max-width: none;
    }
`);

export const ImageBody = styled('div', {
    shouldForwardProp: (props) => !['container'].includes(props),
})(({ theme }) => {
    return `
        display: flex;
        padding-bottom: calc(325px + 20px);
        flex-wrap: wrap;
        gap: 12px;

        ${theme.breakpoints.up('sm')} {
            padding-bottom: 0;
            flex-wrap: nowrap;
        }
    `;
});

export const ImageLoader = styled.div(({ isLoading }) => `
    position: absolute;
    top: 0;
    left: 0;
    transform: none;
    width: 100%;
    height: 100%;
    background: rgba(255,255,255,.75);
    display: ${isLoading ? 'flex' : 'none'};
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
`);

export const ImageUploadWrapper = styled.div(({ theme }) => `
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    position: relative;
    margin-bottom: 1rem;

    .form-label {
        font-size: 0.8rem;
        margin-bottom: 0.75rem;
    }
`);

export const FileWrapper = styled.div`
    margin-right: 0.5rem;

    input[type="file"] {
        display: none;
    }

    .form-control {
        font-size: 0.8rem;
    }
`;

export const ActionWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 0 1rem;

    button {
        margin-bottom: 0.3125rem;
        padding: .5rem .75rem;
    }
`;

export const UploadBtn = styled.div(({ theme }) => `
    display: inline-block;
    margin-bottom: 0.3125rem;
    border-radius: ${theme.rounded.sm};
    width: 100%;

    &:focus {
        outline: 2px solid ${theme.accent2Color}
    }

    .btn.btn-primary {
        margin-bottom: 0;
    }
`);

export const ImagePreviewWrapper = styled('div', {
    shouldForwardProp: (props) => !['container'].includes(props),
})(({ theme, container }) => {
    const itemWidth = getItemWidth(container?.clientWidth);
    
    return `
        position: relative;
        display: flex;
        border-radius: ${theme.rounded.md};
        border: 2px solid ${theme.primaryColor};
        background: ${theme.white};
        overflow: hidden;
        min-width: ${defaultWidth}px;
        max-height: 400px;
        justify-content: center;

        &:hover .image-action__buttons {
            top: 12px;
            opacity: 1;
        }

        ${theme.breakpoints.up('sm')} {
            min-width: ${itemWidth};
        }
    `;
});

export const NubsItem = styled('div', {
    shouldForwardProp: (props) => !['isImage', 'container'].includes(props),
})(({ theme, isImage, container }) => {
    const itemWidth = getItemWidth(container?.clientWidth);
    return `
        background: ${theme.white};
        border-radius: ${theme.rounded.md};
        padding: ${isImage ? '0' : '12px'};
        max-height: 400px;
        min-width: ${isImage ? 'unset' : `${defaultWidth}px`};
        overflow: ${isImage ? 'unset' : 'auto'};
        display: flex;

        .nubs__image {
            min-width: ${defaultWidth}px;
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        .image-thumbnail-loader__preview {
            background: transparent;
            margin-right: 0;
            margin-bottom: 0;
            overflow: hidden;
            border-radius: calc(${theme.rounded.md} - 2px);
            max-height: 100%;
        }

        ${theme.breakpoints.up('sm')} {
            min-width: ${isImage ? 'unset' : `${itemWidth}`};

            .nubs__image {
                min-width: ${itemWidth};
                width: ${itemWidth};
            }
        }

        &:hover .image-action__buttons {
            top: 12px;
            opacity: 1;
        }
    `;
});

export const ImageActionButtons = styled.div(({ theme }) => `
    position: absolute;
    opacity: 0;
    top: -24px;
    right: 12px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 16px;
    background: ${theme.primaryColor};
    border-radius: 6px;
    padding: 0 12px;
    transition: opacity, top .25s;
`);

export const ImageActionButton = styled(Button)(({ theme }) => `
    padding: 0;
    font-size: 24px;
    
    &.btn-link {
        color: ${theme.white};

        &:hover {
            background: transparent;
        }
    }

    svg {
        font-size: 20px;
    }
`);
